import { useState } from "react";
import SiteHeader from "@/components/SiteHeader";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { LOGO_LIGHT } from "@/lib/brand";

const LOGO_URL = LOGO_LIGHT;
const HERO_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_ae92616c-2c62-480e-bb26-954231470e5d/artifacts/91n23cva_IMG_7955.JPG";
const ABOUT_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_ae92616c-2c62-480e-bb26-954231470e5d/artifacts/r1lkubid_IMG_7953.JPG";
const NAIROBI_BG =
  "https://images.pexels.com/photos/24390617/pexels-photo-24390617.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const INFRA_BG =
  "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzb2xhciUyMGZhcm0lMjBpbmZyYXN0cnVjdHVyZXxlbnwwfHx8fDE3ODAxMzA3ODl8MA&ixlib=rb-4.1.0&q=85";
const RESUME_URL =
  "https://customer-assets.emergentagent.com/job_joseph-crm/artifacts/piee4h9t_202605%20Joseph%20Poche%20Resume.pdf";

const experience = [
  {
    period: "2025 — Present",
    role: "Head of Origination — Africa & Emerging Markets",
    org: "Frontera Capital Group Limited",
    location: "Abu Dhabi, UAE",
    bullets: [
      "Lead origination and strategic investment development across SSA infrastructure, energy transition, telecom, digital infrastructure, industrials, financial services and manufacturing.",
      "Originate, screen and structure complex opportunities across infrastructure, energy transition, digital economy and regional growth platforms.",
      "Develop blended-finance and institutional structures involving sovereigns, DFIs, ECAs, commercial lenders, family offices and institutional investors.",
    ],
  },
  {
    period: "2022 — 2025",
    role: "Senior Project Manager — Africa Investments & Regional Strategy",
    org: "Mitsui & Co. Middle East Limited",
    location: "Dubai, UAE",
    bullets: [
      "Supported investment strategy and portfolio management across a diversified Africa-focused portfolio exceeding USD 1.5B.",
      "Evaluated and originated strategic investments across infrastructure, digital economy, industrials, telecom, energy, fintech and logistics.",
      "Led portfolio value-creation initiatives — operational optimization, strategic scaling, partnership development and platform growth.",
    ],
  },
  {
    period: "2009 — 2022",
    role: "Managing Partner & Chief Investment Officer",
    org: "Berkshire Consultancy Limited",
    location: "Nairobi, Kenya",
    bullets: [
      "Founded and scaled a pan-African investment & advisory platform: USD 2B+ in investment, financing and advisory mandates.",
      "Built and managed a 100+ person team across multiple African jurisdictions.",
      "Structured equity, debt, blended-finance and project-finance transactions across frontier and emerging markets.",
    ],
  },
  {
    period: "2006 — 2009",
    role: "Founder & CEO",
    org: "Apaka Limited",
    location: "Nairobi, Kenya",
    bullets: [
      "Founded and scaled a technology-enabled commerce & logistics platform focused on underserved markets.",
      "Led company strategy, fundraising, commercial development, partnerships and operations.",
    ],
  },
];

const capabilities = [
  {
    title: "Private Equity & Direct Investments",
    detail:
      "Growth capital, minority and control transactions, platform investments, co-investments, underwriting, valuation, portfolio construction, exit strategy.",
  },
  {
    title: "Transaction Structuring & Execution",
    detail:
      "Cross-border M&A, equity, debt and quasi-equity structuring, structured finance, project finance, term sheets, due diligence, negotiation.",
  },
  {
    title: "Portfolio Monitoring & Value Creation",
    detail:
      "Portfolio oversight, investee engagement, strategic repositioning, operational scaling, governance enhancement, exit readiness.",
  },
  {
    title: "Infrastructure, Climate & Development Finance",
    detail:
      "Infrastructure finance, energy transition, climate finance, digital infrastructure, blended finance, DFI/IFI engagement, ESG.",
  },
  {
    title: "SSA Market & Stakeholder Leadership",
    detail:
      "Pan-African origination across 30+ countries, sovereigns, regulators, DFIs, co-investors, sponsors — Anglophone and Francophone Africa.",
  },
  {
    title: "Team & Investment Committee",
    detail:
      "Multidisciplinary team leadership, IC processes, coaching and mentoring; senior investor and operator network.",
  },
];

const transactions = [
  {
    title: "USD 500M Fintech Carve-Out & Digital Infrastructure",
    detail: "Telecom assets + regional platform development.",
  },
  {
    title: "USD 150M+ Industrial & Manufacturing Acquisition Platform",
    detail: "Regional consolidation strategy across SSA.",
  },
  {
    title: "USD 30M+ Pan-African Fintech Growth Investment",
    detail: "Growth capital + regional expansion platform.",
  },
  {
    title: "USD 20M+ Telecom Infrastructure Platform",
    detail: "Investment platform with operating partner network.",
  },
];

const highlights = [
  { value: "17+", label: "Years across Sub-Saharan Africa" },
  { value: "USD 2B+", label: "Investment & financing transactions advised" },
  { value: "USD 1.5B+", label: "Africa-focused portfolio exposure" },
  { value: "30+", label: "African countries covered" },
];

export default function Portfolio() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Message sent. Joseph will respond personally.");
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-cream text-navy" data-testid="portfolio-page">
      <Toaster richColors position="top-right" />
      <SiteHeader />

      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-24 md:pb-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <div className="flex items-center gap-3 mb-8">
              <div className="gold-rule" />
              <span className="overline text-gold-deep">Investor · Builder · Operator</span>
            </div>
            <h1 className="editorial-h1 text-5xl sm:text-6xl lg:text-7xl text-navy">
              Joseph <span className="italic">Poche</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-navy/75 leading-relaxed font-light">
              Senior Investment and Strategy Executive — <span className="italic">Private Equity, Direct Equity, Infrastructure
              & Climate Finance</span>. Seventeen years across Sub-Saharan Africa and emerging markets,
              originating and executing institutional capital for growth platforms, climate transition and
              strategic regional infrastructure.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="bg-navy text-gold px-6 py-3 text-xs uppercase tracking-widest hover:bg-navy-600 transition-colors"
              >
                Get in touch
              </a>
              <a
                href="#experience"
                data-testid="hero-cta-experience"
                className="border border-navy text-navy px-6 py-3 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold transition-colors"
              >
                See experience →
              </a>
              <a
                href={RESUME_URL}
                target="_blank"
                rel="noopener noreferrer"
                download="Joseph_Poche_CV.pdf"
                data-testid="hero-cta-cv"
                className="text-navy/70 underline decoration-gold underline-offset-4 px-2 py-3 text-xs uppercase tracking-widest hover:text-navy transition-colors"
              >
                Download CV ↓
              </a>
            </div>

            <div className="mt-12 flex flex-wrap gap-x-10 gap-y-3 text-sm text-navy/70">
              <span>Dubai / Abu Dhabi, UAE</span>
              <span className="text-gold-deep">•</span>
              <span>Nairobi, Kenya</span>
              <span className="text-gold-deep">•</span>
              <a className="underline decoration-gold underline-offset-4" href="mailto:josephpoche13@gmail.com">josephpoche13@gmail.com</a>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-gold/15 hidden md:block" aria-hidden />
            <div className="relative aspect-[4/5] overflow-hidden bg-navy">
              <img
                src={HERO_PORTRAIT}
                alt="Joseph Poche — portrait"
                width="800"
                height="1000"
                fetchpriority="high"
                decoding="async"
                className="w-full h-full object-cover"
                data-testid="hero-portrait"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:flex flex-col items-end bg-cream px-4 py-3 border-l-2 border-gold">
              <div className="overline text-gold-deep">Coverage</div>
              <div className="font-serif italic text-navy">30+ African countries</div>
            </div>
          </div>
        </div>

        {/* Highlights strip */}
        <div className="border-y border-navy/10 bg-cream/50">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 py-10">
            {highlights.map((h, i) => (
              <div key={i} className="" data-testid={`highlight-${i}`}>
                <div className="font-serif text-3xl md:text-4xl text-navy">{h.value}</div>
                <div className="overline text-gold-deep mt-2">{h.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== ABOUT ============== */}
      <section id="about" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16" data-testid="about-section">
        <div className="md:col-span-5 order-2 md:order-1 relative">
          <div className="aspect-[4/5] overflow-hidden">
            <img src={ABOUT_PORTRAIT} alt="Joseph Poche in conversation" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
          <div className="hidden md:block absolute -bottom-6 -left-6 w-40 h-40 border border-gold pointer-events-none" />
        </div>
        <div className="md:col-span-7 order-1 md:order-2">
          <div className="overline text-gold-deep mb-4">Profile</div>
          <h2 className="editorial-h2 text-4xl md:text-5xl text-navy">
            Senior private equity & emerging-markets investment professional.
          </h2>
          <div className="gold-rule mt-8 mb-8" />
          <p className="text-navy/75 leading-relaxed font-light">
            17+ years across Sub-Saharan Africa and emerging markets, combining direct investment origination,
            growth capital, infrastructure investing, blended finance, portfolio oversight, value creation and
            institutional capital mobilization. Proven record advising on and executing USD&nbsp;2B+ in
            investment and financing transactions, with Africa-focused portfolio exposure exceeding USD&nbsp;1.5B.
          </p>
          <p className="mt-4 text-navy/75 leading-relaxed font-light">
            Strong fit with mandates requiring SSA strategy formulation, deal-flow generation, complex equity and
            quasi-equity transactions, portfolio performance management, multidisciplinary team coordination —
            advancing climate, digital, inclusion, governance and sustainable private-sector development outcomes.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
            <div>
              <div className="overline text-gold-deep">Languages</div>
              <div className="mt-1 text-navy/80">English · Kiswahili · French · Spanish</div>
            </div>
            <div>
              <div className="overline text-gold-deep">Education</div>
              <div className="mt-1 text-navy/80">MBA Finance — Strathmore · INSEAD · USIU-Africa · CFA L1 Candidate</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== EXPERIENCE TIMELINE ============== */}
      <section id="experience" className="bg-navy text-cream py-24 md:py-32 relative overflow-hidden" data-testid="experience-section">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: `url(${INFRA_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
          aria-hidden
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="overline text-gold mb-4">Experience</div>
          <h2 className="editorial-h2 text-4xl md:text-5xl">
            A career in <span className="italic text-gold">African capital markets.</span>
          </h2>
          <div className="gold-rule mt-8 mb-16" />

          <div className="relative">
            <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-px bg-gold/40" aria-hidden />
            {experience.map((e, i) => (
              <div key={i} className="relative grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-testid={`experience-${i}`}>
                <div className={`md:text-right ${i % 2 === 0 ? "md:pr-12" : "md:order-2 md:pl-12"}`}>
                  <div className="font-serif italic text-gold text-lg">{e.period}</div>
                  <div className="font-serif text-2xl text-cream mt-2">{e.role}</div>
                  <div className="overline text-cream/70 mt-2">{e.org} · {e.location}</div>
                </div>
                <div className={`pl-10 md:pl-12 ${i % 2 === 0 ? "" : "md:order-1 md:pr-12 md:text-right"}`}>
                  <span className="absolute left-1.5 md:left-1/2 md:-translate-x-1/2 w-3 h-3 bg-gold rounded-full mt-2" aria-hidden />
                  <ul className="space-y-3 text-cream/80 text-sm font-light leading-relaxed">
                    {e.bullets.map((b, bi) => <li key={bi}>— {b}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CAPABILITIES ============== */}
      <section id="capabilities" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10" data-testid="capabilities-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline text-gold-deep mb-4">Core Capabilities</div>
            <h2 className="editorial-h2 text-4xl md:text-5xl text-navy">
              Full-cycle <span className="italic">investment leadership.</span>
            </h2>
            <div className="gold-rule mt-8" />
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
            {capabilities.map((c, i) => (
              <div key={i} data-testid={`capability-${i}`}>
                <div className="font-serif text-xl text-navy">{c.title}</div>
                <p className="mt-3 text-sm text-navy/70 font-light leading-relaxed">{c.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SELECTED TRANSACTIONS ============== */}
      <section className="bg-cream border-y border-navy/10 py-24" data-testid="transactions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="overline text-gold-deep mb-4">Selected Transactions</div>
          <h2 className="editorial-h2 text-4xl md:text-5xl text-navy">Recent <span className="italic">platforms.</span></h2>
          <div className="gold-rule mt-8 mb-12" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {transactions.map((t, i) => (
              <div key={i} className="border-l-2 border-gold pl-6" data-testid={`transaction-${i}`}>
                <div className="font-serif text-2xl text-navy">{t.title}</div>
                <p className="mt-2 text-sm text-navy/70 font-light">{t.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== GEOGRAPHY ============== */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-navy text-cream" data-testid="geography-section">
        <div
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{ backgroundImage: `url(${NAIROBI_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-gold mb-4">Geographic Coverage</div>
            <h2 className="editorial-h2 text-4xl md:text-5xl">
              30+ countries.<br /><span className="italic text-gold">One Africa thesis.</span>
            </h2>
            <div className="gold-rule mt-8 mb-8" />
            <p className="text-cream/80 font-light leading-relaxed max-w-lg">
              East, West, Central, Southern, Anglophone and Francophone Africa. Strong network across DFIs,
              development agencies, sovereign institutions, institutional investors, family offices, financial
              institutions, infrastructure developers, regulators and strategic regional operators.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6 text-sm">
            {["East Africa","West Africa","Central Africa","Southern Africa","Anglophone","Francophone","DFIs & IFIs","Sovereign-linked","Institutional Investors","Family Offices"].map((r, i) => (
              <div key={i} className="border border-gold/30 py-4 px-4">
                <div className="overline text-gold">Region</div>
                <div className="font-serif text-lg mt-1">{r}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== CONTACT ============== */}
      <section id="contact" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12" data-testid="contact-section">
        <div className="md:col-span-5">
          <div className="overline text-gold-deep mb-4">Contact</div>
          <h2 className="editorial-h2 text-4xl md:text-5xl text-navy">Start a <span className="italic">conversation.</span></h2>
          <div className="gold-rule mt-8 mb-8" />
          <p className="text-navy/75 font-light leading-relaxed">
            For investment opportunities, mandates, partnerships or advisory engagements across Sub-Saharan
            Africa — drop a note. All messages reach Joseph directly.
          </p>
          <div className="mt-8 space-y-3 text-sm text-navy/80">
            <div><span className="overline text-gold-deep mr-2">Email</span> josephpoche13@gmail.com</div>
            <div><span className="overline text-gold-deep mr-2">Mobile</span> +971 52 357 7244</div>
            <div><span className="overline text-gold-deep mr-2">LinkedIn</span> linkedin.com/in/joseph-poche</div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={RESUME_URL}
              target="_blank"
              rel="noopener noreferrer"
              download="Joseph_Poche_CV.pdf"
              data-testid="contact-cv-download"
              className="border border-navy text-navy px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold transition-colors"
            >
              Download CV ↓
            </a>
            <a
              href="/press"
              data-testid="contact-press-link"
              className="text-navy/70 underline decoration-gold underline-offset-4 px-2 py-2.5 text-xs uppercase tracking-widest hover:text-navy"
            >
              Press Kit →
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="contact-form">
          <div className="sm:col-span-1">
            <label className="overline text-navy/60">Name</label>
            <input
              required
              data-testid="contact-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="overline text-navy/60">Email</label>
            <input
              required
              type="email"
              data-testid="contact-form-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="overline text-navy/60">Organization</label>
            <input
              data-testid="contact-form-org"
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="overline text-navy/60">Message</label>
            <textarea
              required
              rows={5}
              data-testid="contact-form-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full mt-2 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold resize-none"
            />
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-form-submit"
              className="bg-navy text-gold px-8 py-3 text-xs uppercase tracking-widest hover:bg-navy-600 transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>
      </section>

      {/* ============== FOOTER ============== */}
      <footer className="bg-navy text-cream/70 py-12" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Joseph Poche" loading="lazy" decoding="async" className="h-20 w-20 object-contain" />
            <div>
              <div className="font-serif text-cream text-lg">Joseph Poche</div>
              <div className="overline text-gold">Investor · Builder · Operator</div>
            </div>
          </div>
          <div className="text-xs">© {new Date().getFullYear()} Joseph Poche. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
